import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Typography from '../i18n/LocalizedTypography';
import { getPlanetAbbr } from '../../i18n/astrology';
import eventCatalogJson from '../../data/nadiSafeEvents';
import { translateNadiEventText } from '../../data/nadiEventTranslations';
import { lifeEvents } from '../../utils/lifeEvents';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

type EventPolarity = 'positive' | 'challenging' | 'neutral';

interface EventPlanet {
  name: string;
  role?: string;
  condition?: string;
}

interface NadiEvent {
  id: string;
  name: string;
  polarity: EventPolarity;
  combinations: string[];
  planets: EventPlanet[];
  conditions: string[];
  modifiers: string[];
  notes: string[];
  legacyHouseGroups?: {
    goodHouses: number[];
    badHouses: number[];
    goodSupportiveHouses: number[];
    badSupportiveHouses: number[];
  };
}

interface NadiEventCategory {
  id: string;
  name: string;
  events: NadiEvent[];
}

interface NadiEventCatalog {
  display_rules: { public_disclaimer: string };
  categories: NadiEventCategory[];
}

interface EventAnalysisPanelProps {
  chart: any;
  isPremium: boolean;
  rotationHouse?: number;
}

const eventCatalog = eventCatalogJson as NadiEventCatalog;
const legacyNakshatraCategory: NadiEventCategory = {
  id: 'nakshatra_nadi_life_events',
  name: 'Nakshatra Nadi Life Events',
  events: Object.entries(lifeEvents)
    .filter(([id]) => id !== 'none')
    .map(([id, event]) => ({
      id: `nakshatra_${id}`,
      name: event.label,
      polarity: 'neutral',
      combinations: [],
      planets: [],
      conditions: [],
      modifiers: [],
      notes: [],
      legacyHouseGroups: event,
    })),
};
const EVENT_CATEGORIES = [...eventCatalog.categories, legacyNakshatraCategory];
const PLANET_ORDER = ['KETU', 'VENUS', 'SUN', 'MOON', 'MARS', 'RAHU', 'JUPITER', 'SATURN', 'MERCURY', 'URANUS', 'NEPTUNE', 'PLUTO'];
const COLORS = {
  good: '#4caf50',
  bad: '#f44336',
  supportive: '#2196f3',
  challengingSupport: '#8d6e63',
};
const FREE_CATEGORY_IDS = new Set(['career_work_business']);
const FREE_EVENT_IDS = new Set(['employment_prospects', 'administration_career']);

const normalizePlanet = (value: unknown) => String(value || '').trim().toUpperCase();

const parseHouseSegments = (combination: string) => combination
  .split('+')
  .map((segment) => Array.from(new Set((segment.match(/\b(?:1[0-2]|[1-9])\b/g) || []).map(Number))))
  .filter((segment) => segment.length > 0);

const getEventHouseGroups = (event: NadiEvent) => {
  const good = new Set<number>(event.legacyHouseGroups?.goodHouses || []);
  const bad = new Set<number>(event.legacyHouseGroups?.badHouses || []);
  const goodSupportive = new Set<number>(event.legacyHouseGroups?.goodSupportiveHouses || []);
  const badSupportive = new Set<number>(event.legacyHouseGroups?.badSupportiveHouses || []);

  if (!event.legacyHouseGroups) {
    event.combinations.forEach((combination) => {
      parseHouseSegments(combination).forEach((segment, index) => {
        const target = event.polarity === 'positive'
          ? (index === 0 ? good : goodSupportive)
          : event.polarity === 'challenging'
            ? (index === 0 ? bad : badSupportive)
            : goodSupportive;
        segment.forEach((house) => target.add(house));
      });
    });
  }

  return { good, bad, goodSupportive, badSupportive };
};

const getEntry = (record: Record<string, any>, planetName: string) => {
  const normalized = normalizePlanet(planetName);
  return Object.entries(record || {}).find(([key, value]) => (
    normalizePlanet(key) === normalized || normalizePlanet(value?.planet) === normalized
  ))?.[1];
};

const uniqueHouses = (houses: unknown) => Array.from(new Set(
  (Array.isArray(houses) ? houses : [])
    .map(Number)
    .filter((house) => Number.isInteger(house) && house >= 1 && house <= 12)
));

export default function EventAnalysisPanel({ chart, isPremium, rotationHouse = 1 }: EventAnalysisPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const language = i18n.resolvedLanguage || i18n.language;
  const tr = React.useCallback((text: string) => translateNadiEventText(language, text), [language]);
  const [categoryId, setCategoryId] = React.useState(EVENT_CATEGORIES[0]?.id || '');
  const category = EVENT_CATEGORIES.find((item) => item.id === categoryId) || EVENT_CATEGORIES[0];
  const [eventId, setEventId] = React.useState(category?.events[0]?.id || '');
  const selectedEvent = category?.events.find((item) => item.id === eventId) || category?.events[0];

  React.useEffect(() => {
    if (category && !category.events.some((item) => item.id === eventId)) {
      setEventId(category.events[0]?.id || '');
    }
  }, [category, eventId]);

  if (!category || !selectedEvent) return null;

  const significators = chart?.nadiAnalysis?.planetarySignificators || {};
  const nakshatraNadi = chart?.kpAnalysis?.nakshatraNadi || {};
  const sourceEntries = Object.keys(significators).length > 0 ? significators : nakshatraNadi;
  const orderedEntries = Object.entries(sourceEntries).sort(([keyA, valueA]: [string, any], [keyB, valueB]: [string, any]) => {
    const planetA = normalizePlanet(valueA?.planet || keyA);
    const planetB = normalizePlanet(valueB?.planet || keyB);
    const indexA = PLANET_ORDER.indexOf(planetA);
    const indexB = PLANET_ORDER.indexOf(planetB);
    return (indexA < 0 ? 999 : indexA) - (indexB < 0 ? 999 : indexB) || planetA.localeCompare(planetB);
  });
  const { good, bad, goodSupportive, badSupportive } = getEventHouseGroups(selectedEvent);
  const involvedPlanets = new Set(selectedEvent.planets.map((planet) => normalizePlanet(planet.name)));
  const rotateHouse = (house: number) => ((house - rotationHouse + 12) % 12) + 1;
  const rotateHouses = (houses: unknown) => uniqueHouses(houses).map(rotateHouse);
  const primaryColor = selectedEvent.polarity === 'positive'
    ? COLORS.good
    : selectedEvent.polarity === 'challenging' ? COLORS.bad : COLORS.supportive;
  const supportColor = selectedEvent.polarity === 'challenging' ? COLORS.challengingSupport : COLORS.supportive;
  const houseGroups = [
    { key: 'good', label: 'Good', houses: good, color: COLORS.good },
    { key: 'good-supportive', label: 'Supportive', houses: goodSupportive, color: COLORS.supportive },
    { key: 'bad', label: 'Bad', houses: bad, color: COLORS.bad },
    { key: 'bad-supportive', label: 'Challenging supportive', houses: badSupportive, color: COLORS.challengingSupport },
  ];

  const houseStyle = (house: number) => {
    const actualHouse = ((house + rotationHouse - 2) % 12) + 1;
    if (good.has(actualHouse)) return { color: COLORS.good, highlighted: true };
    if (bad.has(actualHouse)) return { color: COLORS.bad, highlighted: true };
    if (goodSupportive.has(actualHouse)) return { color: COLORS.supportive, highlighted: true };
    if (badSupportive.has(actualHouse)) return { color: COLORS.challengingSupport, highlighted: true };
    return { color: 'inherit', highlighted: false };
  };

  const renderHouses = (houses: unknown, occupiedHouse?: number) => rotateHouses(houses).map((house, index) => {
    const style = houseStyle(house);
    return (
      <Box
        component="span"
        key={`${house}-${index}`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.5,
          color: style.color,
          fontSize: style.highlighted ? '0.95rem' : '0.8rem',
          fontWeight: Number(house) === Number(occupiedHouse) || style.highlighted ? 800 : 'normal',
        }}
      >
        {house}
      </Box>
    );
  });

  return (
    <Card sx={{ border: '2px solid #2e7d52', borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 2 } }}>
        <Typography variant="h5" sx={{ color: '#2e7d52', fontWeight: 800, mb: 0.5 }}>
          {tr('Event Analysis')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tr('Select a category and event to view its traditional Nadi house groups and involved planets.')}
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="nadi-event-category-label">{tr('Category')}</InputLabel>
              <Select
                labelId="nadi-event-category-label"
                value={category.id}
                label={tr('Category')}
                onChange={(event) => {
                  if (isPremium || FREE_CATEGORY_IDS.has(event.target.value)) setCategoryId(event.target.value);
                }}
              >
                {EVENT_CATEGORIES.map((item) => (
                  <MenuItem key={item.id} value={item.id} disabled={!isPremium && !FREE_CATEGORY_IDS.has(item.id)}>
                    {item.id === 'nakshatra_nadi_life_events' ? `${t('astrology.nakshatraNadi')} - ${t('astrology.lifeEvent')}` : tr(item.name)}{!isPremium && !FREE_CATEGORY_IDS.has(item.id) ? ` (${tr('Locked')})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="nadi-event-label">{tr('Event')}</InputLabel>
              <Select
                labelId="nadi-event-label"
                value={selectedEvent.id}
                label={tr('Event')}
                onChange={(event) => {
                  if (isPremium || FREE_EVENT_IDS.has(event.target.value)) setEventId(event.target.value);
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
              >
                {category.events.map((item) => (
                  <MenuItem key={item.id} value={item.id} disabled={!isPremium && !FREE_EVENT_IDS.has(item.id)}>
                    {tr(item.name)}{!isPremium && !FREE_EVENT_IDS.has(item.id) ? ` (${tr('Locked')})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {!isPremium && (
          <Box sx={{ mt: -1, mb: 2, p: 1.5, border: '1px solid #f59e0b', borderRadius: 1.5, bgcolor: '#fff7e6' }}>
            <Typography variant="body2" sx={{ color: '#92400e' }}>{tr('Upgrade to unlock more events')}</Typography>
            <Button
              size="small"
              variant="contained"
              sx={{ mt: 1, fontWeight: 800 }}
              onClick={() => navigate(authState.user ? '/upgrade' : '/login')}
            >
              {authState.user ? t('common.upgrade') : t('common.loginToUpgrade')}
            </Button>
          </Box>
        )}

        <Box sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.default' }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{tr(selectedEvent.name)}</Typography>
            {!selectedEvent.legacyHouseGroups && (
              <Chip
                size="small"
                label={tr(selectedEvent.polarity === 'positive' ? 'Supportive indication' : selectedEvent.polarity === 'challenging' ? 'Challenging indication' : 'Neutral indication')}
                sx={{ bgcolor: primaryColor, color: '#fff', fontWeight: 700 }}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700 }}>
            {tr('Event groups')}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            {selectedEvent.legacyHouseGroups ? houseGroups.filter((group) => group.houses.size > 0).map((group) => (
              <Box key={group.key} sx={{ px: 1, py: 0.5, bgcolor: group.color, color: '#fff', fontSize: '0.78rem', fontWeight: 800, borderRadius: 1 }}>
                {Array.from(group.houses).sort((a, b) => a - b).join(', ')}
              </Box>
            )) : selectedEvent.combinations.map((combination) => (
              <Box key={combination} sx={{ display: 'inline-flex', alignItems: 'center', overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {parseHouseSegments(combination).map((segment, segmentIndex) => (
                  <Box
                    key={`${combination}-${segmentIndex}`}
                    sx={{ px: 1, py: 0.5, bgcolor: segmentIndex === 0 ? primaryColor : supportColor, color: '#fff', fontSize: '0.78rem', fontWeight: 800 }}
                  >
                    {segment.join(', ')}
                  </Box>
                ))}
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: selectedEvent.planets.length ? 1.5 : 0 }}>
            {houseGroups.filter((group) => group.houses.size > 0).map((group) => (
              <Legend key={group.key} color={group.color} label={`${tr(group.label)}: ${Array.from(group.houses).sort((a, b) => a - b).join(', ')}`} />
            ))}
          </Stack>

          {selectedEvent.planets.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>{tr('Planets involved')}</Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                {selectedEvent.planets.map((planet, index) => (
                  <Chip
                    key={`${planet.name}-${index}`}
                    size="small"
                    label={[planet.name, planet.role, planet.condition].filter(Boolean).map((value) => tr(String(value))).join(' — ')}
                    sx={{ borderColor: '#7b1fa2', color: '#6a1b9a', fontWeight: 700 }}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {[...selectedEvent.conditions, ...selectedEvent.modifiers].length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {[...selectedEvent.conditions, ...selectedEvent.modifiers].map(tr).join(' • ')}
            </Typography>
          )}
        </Box>

        <Typography variant="h6" sx={{ color: '#2e7d52', fontWeight: 800, mb: 0.25 }}>
          {t('astrology.nakshatraNadi')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {t('astrology.planetStarSub')}
        </Typography>

        <Grid container spacing={1}>
          {orderedEntries.map(([planetKey, data]: [string, any]) => {
            if (!data) return null;
            const planetName = data.planet || planetKey;
            const starLord = data.starLord;
            const subLord = data.subLord;
            const ownEntry = getEntry(significators, planetName) || data;
            const starEntry = getEntry(significators, starLord) || getEntry(nakshatraNadi, starLord);
            const subEntry = getEntry(significators, subLord) || getEntry(nakshatraNadi, subLord);
            const actualCurrentHouse = Number(data.currentHouse || chart?.planets?.[planetKey]?.bhavaChalitHouse || chart?.planets?.[planetKey]?.house || 0) || undefined;
            const currentHouse = actualCurrentHouse ? rotateHouse(actualCurrentHouse) : undefined;
            const isInvolved = involvedPlanets.has(normalizePlanet(planetName));

            return (
              <Grid key={planetKey} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 0.9, minHeight: 112, bgcolor: isInvolved ? '#f7eefb' : 'background.paper', border: '2px solid', borderColor: isInvolved ? '#9c27b0' : 'divider', borderRadius: 1.25 }}>
                  <Typography sx={{ color: isInvolved ? '#7b1fa2' : '#2e7d52', fontWeight: 800, textAlign: 'center', textDecoration: 'underline', mb: 0.75 }}>
                    {getPlanetAbbr(t, planetName)}{currentHouse ? ` (H${currentHouse}${actualCurrentHouse !== currentHouse ? `/H${actualCurrentHouse}` : ''})` : ''}{isInvolved ? ` • ${tr('involved')}` : ''}
                  </Typography>
                  <PlanetRow label={getPlanetAbbr(t, planetName)} houses={renderHouses(ownEntry?.significators?.planet || data.significators?.planet, currentHouse)} />
                  <PlanetRow label={getPlanetAbbr(t, starLord)} houses={renderHouses(starEntry?.significators?.planet || data.significators?.starLord, starEntry?.currentHouse ? rotateHouse(Number(starEntry.currentHouse)) : undefined)} />
                  <PlanetRow label={getPlanetAbbr(t, subLord)} houses={renderHouses(subEntry?.significators?.planet || data.significators?.subLord, subEntry?.currentHouse ? rotateHouse(Number(subEntry.currentHouse)) : undefined)} />
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
          {tr(eventCatalog.display_rules.public_disclaimer)}
        </Typography>
      </CardContent>
    </Card>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: color }} />
      <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
    </Box>
  );
}

function PlanetRow({ label, houses }: { label: string; houses: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ mb: 0.35, minHeight: 22 }}>
      <Box component="strong" sx={{ mr: 0.5 }}>{label || '—'}:</Box>{houses || '—'}
    </Typography>
  );
}
